import _ from "lodash";
// * external
import React, { useEffect } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Button } from "@toddle-edu/ds-web";
import { TickCircleFilled } from "@toddle-edu/ds-icons";

// * internal
import {
  ResourcesIllustration,
  DocumentAIIllustration,
  TechnicalGlitchIllustration,
  NoWorkbookTemplatesIllustration,
} from "@toddle-edu/ds-theme";
import {
  EXTRACT_FROM_DOC,
  GENERATE_USING_AI,
  ADD_QUESTION_FROM_LIBRARY,
} from "Worksheet/modules/WorksheetConstants";
import { WORKSHEET_QUESTION_CREATION } from "Constants/stringConstants";
import {
  isMYPWorksheetEnabledForLibrary,
  getIsMYPGrade,
  curriculumTypeObj,
} from "routes/routes/ToddleLibrary/modules/ToddleLibraryUtils";
import { getGlobalGradeUid } from "LearningCourses/modules/Utils";

// * relative
import classes from "./WorksheetCreateType.scss";

const setCreateTypeSessionInStorage = type => {
  const sessionStorageKey = "worksheetCreationType";
  sessionStorage.setItem(sessionStorageKey, type);
};

// * Create types for worksheet
const getCreateTypes = (t, showToddleLibraryImport) => [
  {
    type: GENERATE_USING_AI,
    title: t("worksheet:create_with_toddle_ai"),
    subtitle: t("worksheet:generate_questions_with_ai"),
    illustration: NoWorkbookTemplatesIllustration,
  },
  {
    type: EXTRACT_FROM_DOC,
    title: t("worksheet:extract_docs_forms"),
    subtitle: t("worksheet:generate_questions_from_docs_forms"),
    illustration: DocumentAIIllustration,
  },
  ...[
    showToddleLibraryImport
      ? {
          type: ADD_QUESTION_FROM_LIBRARY,
          title: t("toddleLibrary:import_from_toddle_library"),
          subtitle: t("worksheet:generate_questions_using_toddle_library"),
          illustration: ResourcesIllustration,
        }
      : {
          type: ADD_QUESTION_FROM_LIBRARY,
          title: t("worksheet:import_from_school_library"),
          subtitle: t("worksheet:generate_question_using_school_library"),
          illustration: ResourcesIllustration,
        },
  ],
  {
    type: WORKSHEET_QUESTION_CREATION,
    title: t("worksheet:create_from_scratch_capitalize"),
    subtitle: t("worksheet:generate_questions_from_scratch"),
    illustration: TechnicalGlitchIllustration,
  },
];

/**
 *
 * @param {*} params
 * @returns Worksheet Create Type Component
 */
const WorksheetCreateType = props => {
  const {
    value,
    fieldKey,
    updateInputField,
    testId,
    curriculumType,
    isWorksheetAIRevamp = false,
  } = props;

  const { isDP, isMYP } = curriculumTypeObj({ curriculumType });
  const globalGradeUid = getGlobalGradeUid();
  const isEligibleMYPGrade = isMYP && getIsMYPGrade({ globalGradeUid });
  const showToddleLibraryImport =
    isDP || (isEligibleMYPGrade && isMYPWorksheetEnabledForLibrary());
  const { t } = useTranslation();
  const createTypes = getCreateTypes(t, showToddleLibraryImport);

  const selectedType = value || GENERATE_USING_AI;

  const titleClass = classNames(classes.createTypeCardTitle, "text-body");
  const subTitleClass = classNames(
    classes.createTypeCardSubTitle,
    "text-body-s"
  );

  useEffect(() => {
    // * Setting default value initially
    setTimeout(() => {
      setCreateTypeSessionInStorage(GENERATE_USING_AI);
      updateInputField({ [fieldKey]: GENERATE_USING_AI });
    }, 500);
  }, []);

  const onChangeType = type => {
    setCreateTypeSessionInStorage(type);
    updateInputField({ [fieldKey]: type });
  };

  const createTypeCardContainer = classNames(classes.createTypeCardContainer, {
    [classes.createTypeCardContentRevamp]: isWorksheetAIRevamp,
  });

  return (
    <div className={classes.createTypeContainer}>
      {!isWorksheetAIRevamp && (
        <div className="text-label">
          {t("worksheet:create_worksheet_type_prompt")}
        </div>
      )}
      <div className={createTypeCardContainer}>
        {_.map(createTypes, createType => {
          const isSelected = createType.type === selectedType;

          if (
            isWorksheetAIRevamp &&
            createType.type === WORKSHEET_QUESTION_CREATION
          ) {
            return null;
          }

          return (
            <Button
              dsVersion="2.0"
              variant="neutral"
              type="outlined"
              key={createType.type}
              onClick={() => onChangeType(createType.type)}
              className={classNames(classes.createTypeCard, {
                [classes.createTypeCardSelected]: isSelected,
                [classes.createTypeCardRevamped]: isWorksheetAIRevamp,
              })}
              testId={`${testId}-${_.camelCase(createType.type)}`}
            >
              <div className={classes.createTypeCardContent}>
                {isSelected && (
                  <TickCircleFilled
                    width={24}
                    height={24}
                    variant="success"
                    className={classes.selectedCard}
                  />
                )}

                <img src={createType.illustration} alt={createType.title} />
                <div className={titleClass}>{createType.title}</div>
                <div className={subTitleClass}>{createType.subtitle}</div>
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default WorksheetCreateType;
